import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { User, Bell, Palette, Shield, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const roleLabels = {
  super_admin: 'Super Admin',
  group_admin: 'Group Admin',
  mall_admin: 'Mall Admin',
  leasing_head: 'Leasing Head',
  leasing_executive: 'Leasing Executive',
  finance: 'Finance',
  ops: 'Operations',
  marketing: 'Marketing',
};

export default function SettingsPage() {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    leasing: true,
    analytics: false,
    ai: true,
  });
  const [theme, setTheme] = useState('dark');

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Simulate save
    await new Promise((resolve) => setTimeout(resolve, 1000));
    toast.success('Profile updated successfully');
    setSaving(false);
  };

  const handleSaveNotifications = () => {
    toast.success('Notification preferences saved');
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">Settings</h2>
        <p className="text-muted-foreground">Manage your account and preferences</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-muted/30">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="appearance" className="flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Appearance
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Security
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Update your personal details</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveProfile} className="space-y-6">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-3xl font-bold text-primary">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold">{user?.name}</h3>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                    <Badge className="mt-2 bg-primary/20 text-primary">
                      {roleLabels[user?.role] || user?.role}
                    </Badge>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      defaultValue={user?.name}
                      className="input-dark"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      defaultValue={user?.email}
                      className="input-dark"
                      disabled
                      data-testid="profile-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      defaultValue={user?.phone || ''}
                      className="input-dark"
                      placeholder="+91-..."
                      data-testid="profile-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Input
                      id="role"
                      value={roleLabels[user?.role] || user?.role}
                      className="input-dark"
                      disabled
                      data-testid="profile-role-input"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-profile-btn">
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Choose how you want to be notified</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-muted-foreground">
                      Receive updates via email
                    </p>
                  </div>
                  <Switch
                    checked={notifications.email}
                    onCheckedChange={(checked) =>
                      setNotifications({ ...notifications, email: checked })
                    }
                    data-testid="notif-email-switch"
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Push Notifications</p>
                    <p className="text-sm text-muted-foreground">
                      Browser push notifications
                    </p>
                  </div>
                  <Switch
                    checked={notifications.push}
                    onCheckedChange={(checked) =>
                      setNotifications({ ...notifications, push: checked })
                    }
                    data-testid="notif-push-switch"
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Leasing Alerts</p>
                    <p className="text-sm text-muted-foreground">
                      New leads, lease expiry, renewals
                    </p>
                  </div>
                  <Switch
                    checked={notifications.leasing}
                    onCheckedChange={(checked) =>
                      setNotifications({ ...notifications, leasing: checked })
                    }
                    data-testid="notif-leasing-switch"
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Analytics Reports</p>
                    <p className="text-sm text-muted-foreground">
                      Weekly/monthly performance reports
                    </p>
                  </div>
                  <Switch
                    checked={notifications.analytics}
                    onCheckedChange={(checked) =>
                      setNotifications({ ...notifications, analytics: checked })
                    }
                    data-testid="notif-analytics-switch"
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">AI Insights</p>
                    <p className="text-sm text-muted-foreground">
                      AI-generated recommendations
                    </p>
                  </div>
                  <Switch
                    checked={notifications.ai}
                    onCheckedChange={(checked) =>
                      setNotifications({ ...notifications, ai: checked })
                    }
                    data-testid="notif-ai-switch"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  className="btn-primary"
                  onClick={handleSaveNotifications}
                  data-testid="save-notifications-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save Preferences
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Customize how the app looks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="mb-4 block">Theme</Label>
                <div className="flex gap-4">
                  <Button
                    variant={theme === 'dark' ? 'default' : 'outline'}
                    className={`flex-1 h-20 flex-col ${theme === 'dark' ? 'btn-primary' : ''}`}
                    onClick={() => setTheme('dark')}
                    data-testid="theme-dark-btn"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gray-900 border border-gray-700 mb-2" />
                    <span>Dark</span>
                  </Button>
                  <Button
                    variant={theme === 'light' ? 'default' : 'outline'}
                    className={`flex-1 h-20 flex-col ${theme === 'light' ? 'btn-primary' : ''}`}
                    onClick={() => setTheme('light')}
                    data-testid="theme-light-btn"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 mb-2" />
                    <span>Light</span>
                  </Button>
                  <Button
                    variant={theme === 'system' ? 'default' : 'outline'}
                    className={`flex-1 h-20 flex-col ${theme === 'system' ? 'btn-primary' : ''}`}
                    onClick={() => setTheme('system')}
                    data-testid="theme-system-btn"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-gray-900 to-white border mb-2" />
                    <span>System</span>
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="p-4 rounded-lg bg-muted/30 border border-border/40">
                <p className="text-sm text-muted-foreground">
                  Theme switching is currently set to dark mode. Light mode will be available in a future update.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Security</CardTitle>
              <CardDescription>Manage your account security</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Change Password</h4>
                  <div className="grid gap-4 max-w-md">
                    <div className="space-y-2">
                      <Label htmlFor="current">Current Password</Label>
                      <Input
                        id="current"
                        type="password"
                        className="input-dark"
                        data-testid="current-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new">New Password</Label>
                      <Input
                        id="new"
                        type="password"
                        className="input-dark"
                        data-testid="new-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm">Confirm New Password</Label>
                      <Input
                        id="confirm"
                        type="password"
                        className="input-dark"
                        data-testid="confirm-password-input"
                      />
                    </div>
                    <Button className="btn-primary w-fit" data-testid="update-password-btn">
                      Update Password
                    </Button>
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="font-medium mb-2">Two-Factor Authentication</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Add an extra layer of security to your account
                  </p>
                  <Button variant="outline" data-testid="enable-2fa-btn">
                    Enable 2FA
                  </Button>
                </div>

                <Separator />

                <div>
                  <h4 className="font-medium mb-2">Active Sessions</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Manage your active login sessions
                  </p>
                  <div className="p-4 rounded-lg bg-muted/30 border border-border/40">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Current Session</p>
                        <p className="text-sm text-muted-foreground">
                          This device • Active now
                        </p>
                      </div>
                      <Badge className="bg-emerald-500/20 text-emerald-400">Active</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
